import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, CircleAlert } from "lucide-react";
import { useStepPrimaryAction } from "../../../context";
import type { PrescriptionRequestReason } from "../machine";

/**
 * Footer "Weiter zur Prüfung" for both request compositions (eGK and manual):
 * the action sits in the fixed footer slot (KO-08, KO-09) instead of an
 * in-body row with a second "Zurück" (KO-06). It stays enabled; a tap on an
 * incomplete request explains what is missing right above the form.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * const { validationError, clearValidation } = usePrescriptionRequestSubmit({ requestReason, hasRequestText, onSubmit });
 * ```
 */
export function usePrescriptionRequestSubmit({
  requestReason,
  hasRequestText,
  onSubmit,
}: {
  readonly requestReason: PrescriptionRequestReason | undefined;
  readonly hasRequestText: boolean;
  readonly onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const [validationError, setValidationError] = useState<string | undefined>();

  const submit = useCallback(
    function submitPrescriptionRequest() {
      if (!requestReason) {
        setValidationError(
          t("self-service.prescription.medication-selection.reason-validation"),
        );
        return;
      }
      if (!hasRequestText) {
        setValidationError(
          t("self-service.prescription.medication-selection.request-validation"),
        );
        return;
      }
      onSubmit();
    },
    [hasRequestText, onSubmit, requestReason, t],
  );

  useStepPrimaryAction({
    label: t("self-service.prescription.medication-selection.submit"),
    icon: ArrowRight,
    testId: "frontdesk-prescription-medication-submit-btn",
    onClick: submit,
  });

  const clearValidation = useCallback(function clearPrescriptionValidation() {
    setValidationError(undefined);
  }, []);

  return { validationError, clearValidation };
}

/**
 * The validation message of a prescription request, scrolled into view when
 * it appears so a patient who tapped the footer action sees it.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <PrescriptionValidationNotice message={validationError} />
 * ```
 */
export function PrescriptionValidationNotice({
  message,
}: {
  readonly message: string | undefined;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(
    function revealValidationMessage() {
      if (message !== undefined) ref.current?.scrollIntoView?.({ block: "nearest" });
    },
    [message],
  );
  if (message === undefined) return null;
  return (
    <div
      ref={ref}
      role="alert"
      data-testid="frontdesk-prescription-medication-validation-error"
      className="flex items-center gap-3 rounded-2xl border border-destructive-subtle bg-destructive-soft px-5 py-4 text-lg text-foreground"
    >
      <CircleAlert aria-hidden className="size-6 shrink-0 text-destructive-ink" />
      {message}
    </div>
  );
}
